<script setup lang="ts" xmlns="http://www.w3.org/1999/html">
import { NTag } from "naive-ui";
import { computed, ref, watch } from "vue";
import { OpenItem, OpenItemPostPayload } from "../../models/contador";
import { formatJSDate } from "../../utils/dates";
import { useTeam } from "../../hooks/useTeam";
import KeyValuePair from "../KeyValuePair.vue";
import ProperText from "../proper-inputs/proper-text.vue";
import { usePortalBreakpoints } from "../../hooks/usePortalBreakpoints.ts";
const { isDesktop } = usePortalBreakpoints();

const props = defineProps<{
    openItem: OpenItem | null;
}>();

const { employeeMailToName } = useTeam();

const emits = defineEmits<{
    (e: "update:field", field: keyof OpenItemPostPayload, value: string): void;
    (e: "close"): void;
}>();

const localDescriptionValue = ref(props.openItem?.description || "");

/**
 * Returns formatted due date and days until the due date.
 * @example: (assuming today is Aug 26): "today" or "Aug 15 (6 days past due)", "Aug 30 (4 days until due)"
 */
const dueDateInfo = computed(() => {
    if (!props.openItem?.due_date) return { formattedDate: "", daysUntil: "" };

    const now = new Date();
    const dueDate = new Date(props.openItem.due_date);
    const resetDueDate = new Date(dueDate.setHours(0, 0, 0, 0));
    const resetNow = new Date(now.setHours(0, 0, 0, 0));

    const timeDiff = resetDueDate.getTime() - resetNow.getTime();
    const daysUntil = Math.ceil(timeDiff / (1000 * 3600 * 24));
    const formattedDate = formatJSDate(dueDate).RELATIVE_DATE_FORMAT;

    if (daysUntil === 0) {
        return { formattedDate: "today", daysUntil: null };
    } else if (daysUntil < 0) {
        return {
            formattedDate: formattedDate,
            daysUntil: `(${Math.abs(daysUntil)} days past due)`,
        };
    } else {
        return {
            formattedDate: formattedDate,
            daysUntil: `(${daysUntil} days until due)`,
        };
    }
});

const statusInfo = computed(() => {
    const status = props.openItem?.status ? props.openItem.status.toUpperCase() : "UNKNOWN";
    const isOpen = status === "OPEN";

    return { status, isOpen };
});

const assignedEmployee = computed(() => {
    if (!props.openItem?.created_by) return "UNASSIGNED";
    return employeeMailToName(props.openItem.created_by);
});

function onDescriptionInput(val: string) {
    if (val !== props.openItem?.description) {
        emits("update:field", "description", val);
    }
}

watch(
    () => props.openItem?.description,
    (newVal) => {
        localDescriptionValue.value = newVal ?? "";
    },
);
</script>

<template>
    <div
        :class="[
            'w-full bg-gradient-to-b p-10 rounded-t-lg border-b border-border-gray',
            statusInfo.isOpen ? 'from-[#FFF6E5] to-white' : 'from-green-100 to-white',
            {
                'pb-2 !px-6': !isDesktop,
            },
        ]"
    >
        <div
            class="mb-8"
            :class="{
                'mt-8 !mb-3 flex items-start justify-between': !isDesktop,
            }"
        >
            <proper-text
                :model-value="localDescriptionValue"
                placeholder="Description *"
                :size="isDesktop ? 'large' : 'medium'"
                :medium-size="!isDesktop"
                live-preview
                disable-update-on-change
                class="ml-[-13px]"
                @update:model-value="(v) => onDescriptionInput(v as string)"
            />

            <button
                class="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-200 active:bg-slate-300 active:scale-95 outline-none"
                v-if="!isDesktop"
                aria-label="Close"
                @click="$emit('close')"
            >
                <span class="material-symbols-outlined text-slate-600">close</span>
            </button>
        </div>

        <div :class="['flex gap-8', isDesktop ? 'h-16' : 'h-fit mb-3']">
            <div class="flex flex-col">
                <span class="text-label text-base text-gray-dark mb-2">STATUS</span>
                <n-tag
                    :type="statusInfo?.isOpen ? 'warning' : 'success'"
                    :bordered="false"
                    class="px-3 py-1 w-fit"
                >
                    <span :class="isDesktop ? 'font-semibold' : ''">
                        {{ statusInfo.status }}
                    </span>
                </n-tag>
            </div>

            <div class="flex flex-col">
                <div class="text-label text-base text-gray-dark mb-1">DUE DATE</div>
                <div
                    :class="[
                        isDesktop
                            ? 'text-base font-semibold first-letter:capitalize'
                            : 'text-sm font-normal first-letter:capitalize',
                    ]"
                >
                    {{ dueDateInfo.formattedDate }}
                    <span v-if="dueDateInfo.daysUntil" class="text-gray-400 font-normal">{{
                        dueDateInfo.daysUntil
                    }}</span>
                </div>
            </div>

            <key-value-pair label="PROPER" :value="assignedEmployee" />
        </div>
    </div>
</template>
